// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2024-07-17 14:33:25 +0200 (Wed, 17 Jul 2024) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 144669 $
// Purpose     : hooks for presentation module
// =============================================================================
// ---Third-party imports
import React from 'react'

// ---Framework imports
import log from 'uix/logging'
import {mapItems, isString, range, dict, callIfFunc} from 'ldash'
import {
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useDimensions,
  useResizeObserver,
  useCallback,
  useDebounce,
  useState,
  useSelector,
  useResettableState,
  useDelay,
  useRouter,
  useApi,
  usePrevious,
} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {RawLink} from 'uix/components/links'
import {VIcon} from 'graphics'

// ---Local imports
import S from './index.scss'

// ---Exports
// return new values for transition progress only each 100ms
let PROGRESS_DEBOUNCE_MS = 200
if (process.env.NODE_ENV === 'production') {
  // more fps
  PROGRESS_DEBOUNCE_MS = 100
}

const zeroPosition = {x: 0, y: 0}
let intify = ({x, y}) => ({x: Math.floor(x), y: Math.floor(y)})
const getScrollPosition = ({element, boundingElement}) => {
  /* get top left position of element with respect to boundingElement
   */
  let targetPosition = element?.current?.getBoundingClientRect()
  let containerPosition = boundingElement?.current?.getBoundingClientRect()
  return containerPosition
    ? {
        x: (containerPosition.x || 0) - (targetPosition.x || 0),
        y: (containerPosition.y || 0) - (targetPosition.y || 0),
      }
    : targetPosition
    ? {x: targetPosition.left, y: targetPosition.top}
    : zeroPosition
}

const useScrollPosition = (element, boundingElement) => {
  let [pos, setPos] = useState(zeroPosition)
  // return new values only each 5ms (200fps)
  let [position] = useDebounce(pos, 5)

  useLayoutEffect(() => {
    if (boundingElement.current && element.current) {
      let handleScroll = () => setPos(intify(getScrollPosition({element, boundingElement})))
      boundingElement.current?.addEventListener('scroll', handleScroll, {passive: true})

      // initialize once
      handleScroll()

      return () => {
        boundingElement.current?.removeEventListener('scroll', handleScroll)
      }
    }
  }, [boundingElement.current, element.current])
  return position
}

const useScrollProgress = element => {
  let [pos, setPos] = useState(0)
  let [position] = useDebounce(pos, PROGRESS_DEBOUNCE_MS)

  useLayoutEffect(() => {
    if (element.current) {
      let handleScroll = () => {
        let e = element?.current
        let progress = e?.scrollTop || 0
        let total = e?.scrollHeight - e?.offsetHeight || 1
        setPos(Math.floor(Math.min(100, (progress / total) * 100)))
      }
      element.current?.addEventListener('scroll', handleScroll, {passive: true})

      // initialize once
      handleScroll()

      return () => {
        element.current?.removeEventListener('scroll', handleScroll)
      }
    }
  }, [element.current])
  return position
}

const TransitionDetector = ({
  boundingElement,
  debug,
  index,
  boundingHeight,
  onTransition,
  onScrolledChange,
}) => {
  /* build div (before slide) with height=0 for tracking scroll positions
   */
  let element = useRef()
  let scrollPosition = useScrollPosition(element, boundingElement)
  let [sendValues, setSendValues] = useState({progressPercent: 0, isScrolled: false})
  let _onTransition = progressPercent => {
    onTransition({progressPercent})
    setSendValues(sendValues => ({...sendValues, progressPercent}))
  }
  useEffect(() => {
    if (scrollPosition.x == 0 && scrollPosition.y == 0) return // do not send zeroPosition values
    // use 99 instead of 100, setActiveSlide scrolling should not activate the previous slide
    const endPercent = 99
    //~ log.x('%o', boundingHeight + scrollPosition.y)
    let progressPercent = ((boundingHeight + scrollPosition.y) / boundingHeight) * 100
    // only inform consumer if between 0 and 100
    if (progressPercent >= 0 && progressPercent <= endPercent) {
      _onTransition(progressPercent)
    } else {
      // send final 0% and 100% values (if <0 and >endPercent)
      if (sendValues.progressPercent > 0 && sendValues.progressPercent < endPercent) {
        _onTransition(progressPercent < 0 ? 0 : 100)
      }
    }
    let isScrolled = progressPercent >= endPercent
    if (isScrolled != sendValues.isScrolled) {
      setSendValues(sendValues => ({...sendValues, isScrolled}))
      onScrolledChange(isScrolled)
    }

    // if slide changes height, boundingHeight will change
  }, [scrollPosition, boundingHeight])

  let debugS = {outline: '0.5px solid blue', zIndex: 200, position: 'relative', height: '0px'}
  return (
    <div ref={element} style={debug ? debugS : null}>
      {debug && (
        <span style={{color: 'blue', backgroundColor: 'white', float: 'right', fontSize: '12px'}}>
          {`[${index}] `}
          {sendValues.progressPercent.toFixed(2)}%
        </span>
      )}
    </div>
  )
}

const SlideContainer = ({onRef, children, ...props}) => {
  /* capture dom node for slide
   */
  let ref = useRef()
  useEffect(() => {
    onRef(ref)
  }, [])
  return (
    <div {...props} ref={ref}>
      {children}
    </div>
  )
}

const useIdSync = ({enabled, rootNode, slides, activeSlide, setActiveSlide, slideRefs}) => {
  /* syncronize request.id <-> activeSlide
   */
  let {request, replaceQueryParams} = useRouter()
  // direction request.id -> activeSlide is only active if init is true
  let [init, setInit] = useResettableState(true, [rootNode?.otid, request.id])
  // XXX: the direction: request.id -> activeSlide is not really stable
  // disable this direction after 1000ms
  //~ useDelay(() => setInit(false), 1000, [rootNode?.otid])

  let [debouncedActiveSlide] = useDebounce(activeSlide, 500, {leading: false})

  useEffect(() => {
    // direction: activeSlide -> request.id
    if (!init && enabled && slides?.[debouncedActiveSlide]) {
      let id = slides[debouncedActiveSlide].otid
      // prevent changing query param during init on subslides
      let req_is_subslide =
        slides[debouncedActiveSlide].subslides.findIndex(sl => sl.otid == request.id) > -1
      if (!req_is_subslide) {
        replaceQueryParams({id})
      }
    }
  }, [enabled, debouncedActiveSlide])

  useEffect(() => {
    // direction: request.id -> activeSlide
    let isInSubSlides = s => s.subslides.findIndex(sl => request.id == sl.otid) > -1
    let isRoomSlide = s => s.roomStart && request.id == s.parent && !s.isRoomSlide

    if (init && enabled && slides && setActiveSlide) {
      let i = slides.findIndex(s => request.id == s.otid || isRoomSlide(s) || isInSubSlides(s))
      if (i == -1) {
        // goto first if id is presentation root id
        //~ if (slides.find(s => s.ancestors.includes(Number(request.id)))) {
        if (rootNode?.otid == request.id) {
          i = 0
        } // else do nothing
      }
      // wait for available slideRef dom node
      // XXX: we have once checked for "i != activeSlide" but for slideFocus mode we want to ensure
      // that we go to start of current slide independently of slide index change
      if (slideRefs[i]?.current && i > -1) {
        log.info('initial active slide index to %o', i)
        setActiveSlide(i, 'instant')
        setInit(false)
      }
    }
  }, [enabled, slides, request.id, activeSlide, setActiveSlide, init, slideRefs])
}

export const useSubSlideIdSync = ({slide, subslides, onSubSlideChange, active}) => {
  let {request, replaceQueryParams} = useRouter()

  let prevActive = usePrevious(active, false) // init with false
  // switch only if true -> false transition (not on initial false -> true transition)
  let deactivating = prevActive && !active

  // idx = 0: no subslide selected
  // idx = 1: first subslide active
  let [activeSubSlideIdx, _setActiveSubSlideIdx] = useResettableState(0, [request.id, deactivating])

  let setActiveSubSlideIdx = useCallback(
    subSlideIdx => {
      // direction: subslide => request.id
      _setActiveSubSlideIdx(subSlideIdx)
      let sl = slide // subSlideIdx == 0 is main slide (otid)
      if (subSlideIdx > 0) {
        sl = subslides[subSlideIdx - 1]
      }
      if (sl) {
        replaceQueryParams({id: sl.otid})
        callIfFunc(onSubSlideChange, {slide: sl})
      }
    },
    [_setActiveSubSlideIdx, replaceQueryParams]
  )

  useEffect(() => {
    // direction: request.id => subslide
    let idx = subslides.findIndex(sl => sl.otid == request.id)
    if (idx > -1) {
      _setActiveSubSlideIdx(idx + 1)
      callIfFunc(onSubSlideChange, {slide: subslides[idx]})
    } else if (request.id == slide.otid) {
      // main slide
      callIfFunc(onSubSlideChange, {slide})
    }
  }, [request.id, subslides])

  return {activeSubSlideIdx, setActiveSubSlideIdx}
}

// positioned before (minus distance) slide content for scroll-to action
const ScrollTarget = ({distance}) => <div style={{height: 0, marginTop: `-${distance}`}} />

export const useSlides = ({
  rootNode,
  slides,
  debug = false,
  transitionCooldown = 300,
  syncId = false,
}) => {
  // track dimensions of boundingElement
  let [dimensions, boundingElement] = useDimensions({delta: 5})

  let progressPercent = useScrollProgress(boundingElement)
  // track dimensions of slides (XXX: use to update transitions?)
  //~ let [slideDims, setSlideDims] = useState({})
  // track slide dom nodes, reset if slideset changes
  let resetDep = [rootNode?.otid]
  //~ log.x(rootNode)
  let [slideRefs, setSlideRefs] = useResettableState({}, resetDep)
  // scroll to slide
  let setActiveSlide = (index, scrollBehavior) => {
    let ref = slideRefs[index]
    if (ref?.current) {
      setIsScrolled(dict(range(index + 1).map(i => [i, true])))
      // scroll to slide
      let behavior = scrollBehavior || (Math.abs(activeSlide - index) == 1 ? 'smooth' : 'instant')

      // selecting ScrollTarget here (slide 0 uses slideStart instead of contentStart)
      let slideStart = ref.current
      let contentStart = ref.current.firstChild
      let target = index > 0 ? contentStart || slideStart : slideStart
      target.scrollIntoView({behavior, block: 'start', inline: 'nearest'})
    } else {
      log.warn('no slide ref for %s', index)
    }
  }

  // track active transition
  let [trans, setTrans] = useState({progress: null, slide: null, start: false})
  // track if slide idxs are "in the past" with respect to forward scrolling
  let [isScrolled, setIsScrolled] = useResettableState({}, resetDep) // {slideIdx: bool}

  let changeTrans = (i, progressPercent) => {
    setTrans({slide: i, progress: progressPercent, start: trans.progress === null})
  }

  let activeSlide = useMemo(
    () => Math.max(0, ...mapItems(isScrolled, idx => idx, (idx, value) => value)),
    [isScrolled]
  )

  useIdSync({enabled: syncId, rootNode, slides, activeSlide, setActiveSlide, slideRefs})

  let transitionDone = () => trans.progress == 100 || trans.progress == 0
  useDelay(
    () => (transitionDone() ? setTrans({progress: null, slide: null, start: false}) : null),
    transitionCooldown,
    [transitionDone()]
  )
  // XXX: explain when this is important
  if (trans.slide === activeSlide && trans.progress == 100) {
    // keep last slide active until above delay finishes transition
    activeSlide = trans.slide - 1
  }

  let buildSlideContainer = ({key, index, content, ...props}) => {
    return (
      <React.Fragment key={key}>
        {index > 0 && (
          <TransitionDetector
            index={index}
            debug={debug}
            boundingElement={boundingElement}
            boundingHeight={dimensions.height}
            onTransition={({progressPercent}) => changeTrans(index, progressPercent)}
            onScrolledChange={value => setIsScrolled({...isScrolled, [index]: value})}
          />
        )}
        <SlideContainer
          {...props}
          onRef={ref => {
            setSlideRefs(slideRefs => ({...slideRefs, [index]: ref}))
          }}
        >
          {index > 0 && <ScrollTarget distance="2em" />}
          {content}
        </SlideContainer>
      </React.Fragment>
    )
  }

  return {
    transitionProgress: trans.progress,
    transitionStart: trans.start,
    progressPercent,
    activeSlide,
    setActiveSlide,
    dimensions,
    boundingElement,
    buildSlideContainer,
    debugData: {progressPercent, activeSlide, trans, dimensions, slides},
  }
}

const stringOrDangerHtml = s => {
  if (isString(s) && s.includes('<') && s.includes('>')) {
    return (s = <span dangerouslySetInnerHTML={{__html: s}} />)
  } else return s
}

export const useCaptionLink = (caption, linkid, url, id, icon) => {
  let apiurl = useSelector(Sel.apiurl)
  let link = linkid || id
  if (caption) {
    if (url) {
      return (
        <p className={S.caption}>
          {stringOrDangerHtml(caption)} {icon && <VIcon name="link" href={url} target="_blank" />}
        </p>
      )
    }
    if (link) {
      return (
        <p className={S.caption}>
          {stringOrDangerHtml(caption)}
          {icon && ' '}
          {icon && <VIcon name="link" href={apiurl.baseUrl('/' + link)} target="_blank" />}
        </p>
      )
    } else {
      return <p className={S.caption}>{stringOrDangerHtml(caption)}</p>
    }
  } else {
    return null
  }
}

export const usePageIdMdCaption = pageid => {
  let {data, loading} = useApi('filter/ancestorAxisJson', {id: pageid}, {condition: !!pageid})
  return data?.ancestorAxis.filter(e => e.OT_RCV_DATATYPE == 1).at(0)?.OT_CAPTION
}
